import React from "react";
import {
  ModuleDto,
  ManagedFragmentDto,
  FragmentActionDto,
  ValidateFormPayloadDto
} from "../../general/dtos";

interface ModuleFlowProps {
  module: ModuleDto;
}

interface Transition {
  targetKey: string;
  label: string;
  isConditional: boolean;
  color: string;
}

export function ModuleFlow({ module }: ModuleFlowProps) {
  const fragments = module.fragments || [];

  // --- 1. LAYOUT CONFIGURATION ---
  const BOX_WIDTH = 150;
  const BOX_HEIGHT = 50;
  const VERTICAL_GAP = 90;
  const CENTER_X = 200; 
  const CURVE_OFFSET_BASE = 80;

  const getCoords = (index: number) => ({
    x: CENTER_X,
    y: index * (BOX_HEIGHT + VERTICAL_GAP) + 40
  });

  const TOTAL_HEIGHT = fragments.length * (BOX_HEIGHT + VERTICAL_GAP) + 40;
  const TOTAL_WIDTH = CENTER_X + BOX_WIDTH + CURVE_OFFSET_BASE; 

  // --- 2. TRANSITION RESOLUTION LOGIC ---
  const resolveTransitions = (
    fragment: ManagedFragmentDto,
    action: FragmentActionDto
  ): Transition[] => {
    const transitions: Transition[] = [];
    const idx = fragments.findIndex(x => x.key === fragment.key);

    const getVisibleNeighbor = (direction: 1 | -1) => {
      let currentIdx = idx + direction;
      while (currentIdx >= 0 && currentIdx < fragments.length) {
        if (!fragments[currentIdx].hidden) return fragments[currentIdx].key;
        currentIdx += direction;
      }
      return null;
    };

    if (action.type === "gotoNext") {
      const target = getVisibleNeighbor(1);
      if (target) {
        transitions.push({ 
          targetKey: target, 
          label: "Next", 
          isConditional: false, 
          color: "var(--primary-color)" 
        });
      }
    } 
    else if (action.type === "gotoPrevious") {
      const target = getVisibleNeighbor(-1);
      if (target) {
        transitions.push({ 
          targetKey: target, 
          label: "Back", 
          isConditional: false, 
          color: "var(--text-color-secondary)" 
        });
      }
    } 
    else if (action.type === "gotoFragment") {
      if (action.to && action.to !== "self") {
        transitions.push({ 
          targetKey: action.to, 
          label: action.label || "Jump", 
          isConditional: false, 
          color: "var(--highlight-bg)" 
        });
      }
    }
    else if (action.type === "validateForm") {
      const payload = action.payload as ValidateFormPayloadDto;
      if (action.to && action.to !== "self") {
        transitions.push({ targetKey: action.to, label: "On close", isConditional: false, color: "var(--primary-color)" });
      }
      if (payload?.toOnAllCorrect && payload.toOnAllCorrect !== "self") {
        transitions.push({ targetKey: payload.toOnAllCorrect, label: "Correct", isConditional: true, color: "var(--green-500)" });
      }
      if (payload?.toOnIncorrect && payload.toOnIncorrect !== "self") {
        transitions.push({ targetKey: payload.toOnIncorrect, label: "Error", isConditional: true, color: "var(--red-500)" });
      }
    }
    return transitions;
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.flow, height: TOTAL_HEIGHT, width: TOTAL_WIDTH }}>
        
        {/* FRAGMENTS */}
        {fragments.map((f, i) => {
          const { x, y } = getCoords(i);
          return (
            <div
              key={f.key}
              style={{
                ...styles.fragmentBox,
                left: x - (BOX_WIDTH / 2),
                top: y - (BOX_HEIGHT / 2),
                width: BOX_WIDTH,
                height: BOX_HEIGHT,
                opacity: !!f.hidden ? 0.5 : 1,
                borderStyle: !!f.hidden ? "dashed" : "solid",
              }}
            >
              <div style={styles.titleText}>{f.title}</div>
              {!!f.hidden && <div style={styles.hiddenLabel}>(Hidden)</div>}
              {f.formFields && f.formFields.length > 0 && <div style={styles.formTag}>Form</div>}
            </div>
          );
        })}

        {/* ARROWS & LABELS */}
        <svg style={{ ...styles.svg, height: TOTAL_HEIGHT, width: TOTAL_WIDTH }}>
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="var(--text-color)" />
            </marker>
          </defs>

          {fragments.map((fragment, i) =>
            fragment.actions.flatMap(action => {
              const transitions = resolveTransitions(fragment, action);
              return transitions.map((t, tIdx) => {
                const targetIdx = fragments.findIndex(f => f.key === t.targetKey);
                if (targetIdx < 0) return null;

                const start = getCoords(i);
                const end = getCoords(targetIdx);
                
                const isImmediateNext = targetIdx === i + 1;
                const isBackward = targetIdx < i;
                const distance = Math.abs(targetIdx - i);

                let path = "";
                let labelX = start.x;
                let labelY = (start.y + end.y) / 2;

                if (isImmediateNext && !t.isConditional) {
                  // DIRECT NEXT (Vertical Line)
                  path = `M ${start.x} ${start.y + (BOX_HEIGHT / 2)} L ${end.x} ${end.y - (BOX_HEIGHT / 2)}`;
                  labelX = start.x + 25; // Offset label slightly to the right of the vertical line
                } else if (isBackward) {
                  // BACK LOOPS (Left Side)
                  const curveOffset = CURVE_OFFSET_BASE + (distance * 15);
                  path = `M ${start.x - (BOX_WIDTH / 2)} ${start.y} 
                          C ${start.x - curveOffset} ${start.y}, 
                            ${end.x - curveOffset} ${end.y}, 
                            ${end.x - (BOX_WIDTH / 2)} ${end.y}`;
                  labelX = start.x - (curveOffset / 1.4);
                } else {
                  // FORWARD JUMPS & CONDITIONALS (Right Side)
                  const curveOffset = CURVE_OFFSET_BASE + (distance * 15);
                  path = `M ${start.x + (BOX_WIDTH / 2)} ${start.y} 
                          C ${start.x + curveOffset} ${start.y}, 
                            ${end.x + curveOffset} ${end.y}, 
                            ${end.x + (BOX_WIDTH / 2)} ${end.y}`;
                  labelX = start.x + (curveOffset / 1.4);
                }

                return (
                  <g key={`${action.key}-${t.targetKey}-${tIdx}`}>
                    <path
                      d={path}
                      stroke={t.color}
                      fill="transparent"
                      strokeWidth={2}
                      strokeDasharray={t.isConditional ? "5,5" : "0"}
                      markerEnd="url(#arrowhead)"
                      opacity={0.7}
                    />
                    <text
                      x={labelX}
                      y={labelY}
                      fill={t.color}
                      fontSize="0.65rem"
                      textAnchor="middle"
                      style={styles.svgText}
                    >
                      {t.label}
                    </text>
                  </g>
                );
              });
            })
          )}
        </svg>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { 
    width: "100%", 
    maxHeight: "650px", 
    overflowY: "auto", 
    overflowX: "auto",
    padding: "1.5rem", 
    border: "1px solid var(--surface-border)",
    borderRadius: "12px",
    background: "var(--surface-ground)"
  },
  flow: { position: "relative" },
  fragmentBox: {
    position: "absolute",
    borderRadius: "8px",
    border: "1px solid var(--surface-border)",
    background: "var(--surface-card)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
  },
  titleText: { fontSize: "0.75rem", fontWeight: 700, textAlign: "center", padding: "0 8px", lineHeight: 1.2 },
  hiddenLabel: { fontSize: "0.55rem", opacity: 0.6, marginTop: "2px", fontWeight: 400 },
  formTag: {
    position: "absolute",
    top: "-10px",
    right: "-10px",
    background: "var(--primary-color)",
    color: "#fff",
    fontSize: "0.55rem",
    padding: "2px 6px",
    borderRadius: "4px",
    fontWeight: 800,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
  },
  svg: { position: "absolute", top: 0, left: 0, pointerEvents: "none" },
  svgText: { 
    fontWeight: 800, 
    paintOrder: 'stroke', 
    stroke: 'var(--surface-ground)', 
    strokeWidth: '4px',
    strokeLinejoin: 'round'
  }
};